import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Pressable } from 'react-native';
import { Text } from '@/components/ui/text';
import { Input } from '@/components/ui/input';
import { Minus, Plus } from 'lucide-react-native';
import { Icon } from '@/components/ui/icon';
import { useMutation } from '@tanstack/react-query';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { logger } from '@/lib/logger';
import type { WorkoutSetValueRecord } from '~/powersync/generated/schema';

interface WorkoutSetValueProps {
  /**
   * The current value
   */
  value: number;
  /**
   * Callback when value changes
   */
  onValueChange: (value: number) => void;
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
}

export const WorkoutSetValue: React.FC<WorkoutSetValueProps> & {
  Synced: typeof WorkoutSetValueSynced;
} = React.memo(
  ({ value, onValueChange, disabled = false }: WorkoutSetValueProps) => {
    const [localValue, setLocalValue] = useState(value.toString());
    const debounceTimerRef = useRef<number | null>(null);

    // Update local value when external value changes (but not during active editing)
    useEffect(() => {
      setLocalValue(value.toString());
    }, [value]);

    // Cleanup debounce timer
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    const syncToDatabase = useCallback(
      (numValue: number) => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          onValueChange(numValue);
        }, 500);
      },
      [onValueChange],
    );

    const handleIncrement = () => {
      const currentValue = parseFloat(localValue) || 0;
      const newValue = Math.min(1000, currentValue + 1);
      setLocalValue(newValue.toString());
      syncToDatabase(newValue);
    };

    const handleDecrement = () => {
      const currentValue = parseFloat(localValue) || 0;
      const newValue = Math.max(0, currentValue - 1);
      setLocalValue(newValue.toString());
      syncToDatabase(newValue);
    };

    const handleInputChange = (text: string) => {
      // Update local state immediately
      setLocalValue(text);

      // Parse and validate the number
      const cleanText = text.replace(/[^0-9.,]/g, '');
      const numericValue = parseFloat(cleanText.replace(',', '.'));

      if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 1000) {
        syncToDatabase(numericValue);
      } else if (cleanText === '' || cleanText === '0') {
        syncToDatabase(0);
      }
    };

    return (
      <View className="flex-row items-center bg-background rounded-lg px-1">
        {/* Minus glyph */}
        <Pressable
          onPress={handleDecrement}
          disabled={disabled || value <= 0}
          className="p-2"
        >
          <Icon as={Minus} size={14} className="text-text-secondary" />
        </Pressable>

        {/* Input Field — transparent, no border */}
        <Input
          testID="set-value-input"
          value={localValue}
          onChangeText={handleInputChange}
          keyboardType="numeric"
          textAlign="center"
          editable={!disabled}
          // Select the whole value on focus so typing replaces it instead of appending
          // to the existing number (tapping in gave e.g. "100" + "10" = "10010").
          selectTextOnFocus
          className="flex-1 bg-transparent border-0 text-center text-text-primary px-0 py-1 h-auto web:focus-visible:ring-0"
          style={{ textAlignVertical: 'center' }}
        />

        {/* Plus glyph */}
        <Pressable
          onPress={handleIncrement}
          disabled={disabled || value >= 1000}
          className="p-2"
        >
          <Icon as={Plus} size={14} className="text-text-secondary" />
        </Pressable>
      </View>
    );
  },
);

interface WorkoutSetValueSyncedProps {
  /**
   * The set ID
   */
  setId: string;
  /**
   * The exercise dimension ID
   */
  exerciseDimensionId: string;
  /**
   * The workout ID for creating values (denormalized field)
   */
  workoutId: string;
}

const WorkoutSetValueSynced: React.FC<WorkoutSetValueSyncedProps> = ({
  setId,
  exerciseDimensionId,
  workoutId,
}) => {
  const powerSync = usePowerSync();

  // Fetch the current set value using clean local-api
  const {
    data: setValues,
    isLoading,
    error,
  } = useLivePowerSyncQuery<WorkoutSetValueRecord>(
    db.Workout.queries.findSetValue(setId, exerciseDimensionId),
    [setId, exerciseDimensionId],
  );

  const currentSetValue = setValues?.[0];
  const value = currentSetValue?.value || 0;

  // Clean local-api mutation for updating/creating set value
  const updateValueMutation = useMutation({
    mutationFn: (newValue: number) =>
      db.Workout.mutations.upsertSetValue(
        powerSync,
        setId,
        exerciseDimensionId,
        newValue,
        workoutId,
      ),
  });

  const handleValueChange = (newValue: number) => {
    updateValueMutation.mutate(newValue);
  };

  if (isLoading) {
    return (
      <View className="h-8 flex items-center justify-center">
        <Text className="text-xs text-text-muted">...</Text>
      </View>
    );
  }

  if (error) {
    logger.error('Failed to load set value:', error);
    return (
      <View className="h-8 flex items-center justify-center">
        <Text className="text-xs text-destructive">Error</Text>
      </View>
    );
  }

  return (
    <WorkoutSetValue
      value={value}
      onValueChange={handleValueChange}
      disabled={updateValueMutation.isPending}
    />
  );
};

// Attach Synced as a static property
WorkoutSetValue.Synced = WorkoutSetValueSynced;
